import React, { FC, useEffect, useState } from 'react';
import NcImage from 'shared/NcImage/NcImage';
import Explore1 from 'images/instaacard/home/start-exploring/explore-1.png';
import Explore2 from 'images/instaacard/home/start-exploring/explore-2.png';
import Explore3 from 'images/instaacard/home/start-exploring/explore-3.png';
import Explore4 from 'images/instaacard/home/start-exploring/explore-4.png';
import Explore5 from 'images/instaacard/home/start-exploring/explore-5.png';

import { fetchHomeFeatureSection } from 'service/homeFeatureApi';

interface EcoSystemItem {
    id: number;
    img: string;
    imgDark: string;
    title: string;
    desc: string;
    /** Optional highlight line rendered between the title and the description. */
    highlight?: string;
}

export interface SectionHowItWorkProps {
    className?: string;
    data?: EcoSystemItem[];
}

const DEMO_DATA: EcoSystemItem[] = [
    {
        id: 1,
        img: Explore1,
        imgDark: Explore1,
        title: 'Your Digital Profile',
        desc: 'Everything you want to share, all in one place. Use the INSTAACARD App to create, update and manage your digital profile — from contact information and social links to your professional presence.'
    },
    {
        id: 2,
        img: Explore2,
        imgDark: Explore2,
        title: 'Multiple Profiles',
        desc: 'Manage multiple professional identities from one place. Create and manage separate profiles for different professional or personal needs directly through the INSTAACARD App.'
    },
    {
        id: 3,
        img: Explore3,
        imgDark: Explore3,
        title: 'Digital Connections',
        desc: 'Everything you want to share, connected through one smart profile. Your INSTAACARD brings your contact details, social profiles, professional information and links together in one place.'
    },
    {
        id: 4,
        img: Explore4,
        imgDark: Explore4,
        title: 'One Profile. Multiple Ways To Connect.',
        highlight: 'NFC • QR Code • Smart Link',
        desc: 'No matter how someone receives your profile, they can instantly view your information without needing the INSTAACARD App.'
    },
    {
        id: 5,
        img: Explore5,
        imgDark: Explore5,
        title: 'A Profile That Does More',
        desc: 'Give people more than just your phone number. Showcase your contact information, social media, website, portfolio, business information, professional details and other important links.'
    }
];

/** The bundled images, by the filename the admin panel stores as `imageKey`. */
const BUNDLED_IMAGES: Record<string, string> = {
    'explore-1.png': Explore1,
    'explore-2.png': Explore2,
    'explore-3.png': Explore3,
    'explore-4.png': Explore4,
    'explore-5.png': Explore5
};

const SectionHowItWork: FC<SectionHowItWorkProps> = ({ className = '', data = DEMO_DATA }) => {
    /**
     * Content is editable from the admin panel, but the compiled-in copy above is
     * the floor and stays in the bundle permanently.
     *
     * `items` starts as DEMO_DATA and is only replaced once a usable payload
     * arrives, so the section renders complete on the first frame and never
     * shows a spinner, a gap, or a half-built row. This is above the fold on the
     * home page — a content API being slow, down, or empty must not be able to
     * leave a hole in it.
     */
    const [items, setItems] = useState<EcoSystemItem[]>(data);

    useEffect(() => {
        let cancelled = false;

        fetchHomeFeatureSection('features').then((remote) => {
            // Empty means "the fetch failed or there is nothing active" — either
            // way, keep what is already on screen.
            if (cancelled || !remote.length) return;

            setItems(
                remote.map((feature, index) => {
                    // Prefer the admin upload; fall back to the bundled asset it
                    // names; fall back again to whatever this slot shipped with,
                    // so a row that names an image the build does not have still
                    // renders a picture rather than a broken-image icon.
                    const bundled = feature.imageKey ? BUNDLED_IMAGES[feature.imageKey] : undefined;
                    const fallback = DEMO_DATA[index % DEMO_DATA.length].img;
                    const img = feature.imageUrl || bundled || fallback;

                    return {
                        id: index + 1,
                        img,
                        imgDark: img,
                        title: feature.title,
                        desc: feature.description || '',
                        ...(feature.highlight ? { highlight: feature.highlight } : {})
                    };
                })
            );
        });

        return () => {
            cancelled = true;
        };
    }, [data]);

    const renderContent = (item: EcoSystemItem) => (
        <div className="text-center space-y-2">
            <h3 className="text-icBlue-100 font-bold">{item.title}</h3>
            {item.highlight && <span className="block text-icBlue-100 font-semibold text-sm">{item.highlight}</span>}
            <span className="block text-slate-600 dark:text-slate-400 text-sm leading-6">{item.desc}</span>
        </div>
    );

    return (
        <div className={`nc-SectionHowItWork ${className}`} data-nc-id="SectionHowItWork">
            <h2 className="font-semibold text-3xl sm:text-3xl xl:text-4xl mt-6 sm:mt-10 !leading-[1.2] tracking-tight text-center pb-16">
                Five distinctive attributes of INSTAACARD
            </h2>
            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 sm:gap-10 lg:gap-6 xl:gap-8">
                {items.map((item: EcoSystemItem) => (
                    <div key={item.id} className="relative flex flex-col items-center w-full max-w-xs mx-auto">
                        <NcImage containerClassName="mb-6 max-w-[140px] mx-auto" className="rounded-3xl" src={item.img} />
                        {renderContent(item)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SectionHowItWork;
